import React, { useEffect, useRef, useState } from "react";

const SlidesView = ({
  pdfDoc,
  currentPage,
  totalPages,
  rotation,
  onPageChange,
  onClose,
}) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const nextCanvasRef = useRef(null);
  const [isRendering, setIsRendering] = useState(false);
  const [activeCanvas, setActiveCanvas] = useState("current"); // "current" or "next"
  const slideFsRef = useRef(null);
  const isInitialRender = useRef(true);
  const previousPageRef = useRef(null);
  const renderTasksRef = useRef(new Map()); // Track render tasks per canvas: { canvas: renderTask }

  // Calculate scale to fit page to entire screen
  const calculateFitScale = async (pageNum) => {
    if (!pdfDoc || !containerRef.current) return 1;

    try {
      const page = await pdfDoc.getPage(pageNum);
      const unscaledViewport = page.getViewport({ scale: 1, rotation });
      const container = containerRef.current;
      
      // Use full viewport dimensions (entire screen)
      const availableWidth = container.clientWidth;
      const availableHeight = container.clientHeight;
      
      // Calculate scale to fit both width and height
      const scaleX = availableWidth / unscaledViewport.width;
      const scaleY = availableHeight / unscaledViewport.height;
      
      // Use the smaller scale to ensure page fits completely on screen
      const fitScale = Math.min(scaleX, scaleY);
      
      return Math.max(0.1, fitScale);
    } catch (err) {
      console.error("Error calculating fit scale:", err);
      return 1;
    }
  };

  // Render a page to a specific canvas
  const renderPageToCanvas = async (pageNum, canvas) => {
    if (!pdfDoc || !canvas || !containerRef.current) return;

    try {
      // Cancel any existing render task for this canvas
      const existingTask = renderTasksRef.current.get(canvas);
      if (existingTask && existingTask.cancel) {
        existingTask.cancel();
      }
      
      const page = await pdfDoc.getPage(pageNum);
      const fitScale = await calculateFitScale(pageNum);
      const viewport = page.getViewport({ scale: fitScale, rotation });
      const context = canvas.getContext("2d");
      
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      // Start render and store the task
      const renderTask = page.render({ canvasContext: context, viewport });
      renderTasksRef.current.set(canvas, renderTask);
      
      await renderTask.promise;
      
      // Clear the task after successful render
      renderTasksRef.current.delete(canvas);
      return true;
    } catch (err) {
      // Only log if it's not a cancellation error
      if (err.name !== "RenderingCancelledException") {
        console.error("Error rendering page in slides view:", err);
      }
      // Clear the task on error
      renderTasksRef.current.delete(canvas);
      return false;
    }
  };

  // Render the current page with smooth transition
  const renderPage = async (pageNum) => {
    if (!pdfDoc || !canvasRef.current || !containerRef.current || isRendering) return;

    try {
      setIsRendering(true);
      
      // On initial render, render directly to current canvas without transition
      if (isInitialRender.current) {
        await renderPageToCanvas(pageNum, canvasRef.current);
        isInitialRender.current = false;
        previousPageRef.current = pageNum;
      } else {
        // For subsequent renders, use smooth transition
        if (!nextCanvasRef.current) return;
        
        // Determine which canvas is currently visible and which to render to
        const targetCanvas = activeCanvas === "current" ? nextCanvasRef.current : canvasRef.current;
        
        // Render the new page to the hidden canvas
        await renderPageToCanvas(pageNum, targetCanvas);
        
        // Small delay to ensure render is complete before transition
        await new Promise(resolve => setTimeout(resolve, 10));
        
        // Switch the active canvas with a fade transition
        setActiveCanvas(activeCanvas === "current" ? "next" : "current");
        previousPageRef.current = pageNum;
      }
      
    } catch (err) {
      console.error("Error rendering page in slides view:", err);
    } finally {
      setIsRendering(false);
    }
  };

  // Render page when pdfDoc, currentPage, or rotation changes
  useEffect(() => {
    if (pdfDoc && currentPage >= 1 && currentPage <= totalPages) {
      renderPage(currentPage);
    }
    
    // Cleanup: cancel any ongoing renders when component unmounts or dependencies change
    return () => {
      renderTasksRef.current.forEach((task) => {
        if (task && task.cancel) {
          task.cancel();
        }
      });
      renderTasksRef.current.clear();
    };
  }, [pdfDoc, currentPage, rotation]);

  // Try to enter fullscreen on mount and exit on unmount
  useEffect(() => {
    const el = containerRef.current;
    slideFsRef.current = el;

    const requestFs = async () => {
      if (!el) return;
      if (!document.fullscreenElement && el.requestFullscreen) {
        try {
          await el.requestFullscreen();
        } catch (err) {
          console.warn("Slides fullscreen request failed:", err);
        }
      }
    };

    requestFs();

    return () => {
      // Only exit if we own the fullscreen element
      if (document.fullscreenElement === slideFsRef.current) {
        document.exitFullscreen?.();
      }
    };
  }, []);

  // If user exits fullscreen (e.g., presses Esc), close slides view
  useEffect(() => {
    const handleFsChange = () => {
      const el = slideFsRef.current;
      if (el && document.fullscreenElement !== el) {
        onClose?.();
      }
    };

    document.addEventListener("fullscreenchange", handleFsChange);
    return () => document.removeEventListener("fullscreenchange", handleFsChange);
  }, [onClose]);

  // Recalculate scale on window resize
  useEffect(() => {
    const handleResize = () => {
      if (pdfDoc && currentPage >= 1 && currentPage <= totalPages) {
        renderPage(currentPage);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [pdfDoc, currentPage, rotation]);

  // Handle arrow key navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle arrow keys if not typing in an input/textarea
      if (
        e.target.tagName === "INPUT" ||
        e.target.tagName === "TEXTAREA" ||
        e.target.isContentEditable
      ) {
        return;
      }

      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          if (currentPage > 1) {
            onPageChange(currentPage - 1);
          }
          break;
        case "ArrowRight":
          e.preventDefault();
          if (currentPage < totalPages) {
            onPageChange(currentPage + 1);
          }
          break;
        case "Escape":
          e.preventDefault();
          onClose();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentPage, totalPages, onPageChange, onClose]);

  return (
    <div className="pdf-slides-view" ref={containerRef}>
      <div className="pdf-slides-canvas-container">
        <canvas 
          ref={canvasRef} 
          className={`pdf-slides-canvas ${activeCanvas === "current" ? "pdf-slides-canvas-active" : "pdf-slides-canvas-hidden"}`}
        />
        <canvas 
          ref={nextCanvasRef} 
          className={`pdf-slides-canvas ${activeCanvas === "next" ? "pdf-slides-canvas-active" : "pdf-slides-canvas-hidden"}`}
        />
      </div>
    </div>
  );
};

export default SlidesView;
